// React & Hooks
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// API & Types
import { DataWithPagination } from "services/api";
import { getFleetVehicleOfferRentsAPI } from "services/api/fleet/vehicles/offers/api";
import { FleetVehicleOfferRentRow } from "services/api/fleet/vehicles/offers/types";
import { initialDataWithPagination } from "services/api/consts";
import { DropdownCacheKey, buildDropdownCacheKey, useDropdownCache } from "services/hooks/useDropdownCache";

// Component types
import { FleetOfferRentDropdownProps } from "./types";
import { DropdownOption } from "../Dropdown/types";

// UI Components
import { DropdownAutocomplete } from "../DropdownAutocomplete";

const FleetOfferRentDropdown: React.FC<FleetOfferRentDropdownProps> = (props) => {
  const {
    name,
    vehicleId,
    disabled,
    required,
    label,
    error,
    value,
    direction,
    width,
    height,
    placeholder,
    onChange,
    onOfferSelect,
    showLinkedToContract,
    showCancelled,
    autoSelectLast,
    onLoaded,
  } = props;

  const [searchValue, setSearchValue] = useState<string>("");
  const autoSelectedRef = useRef<boolean>(false);
  const pagination = initialDataWithPagination.pagination;

  const cacheKey = buildDropdownCacheKey(DropdownCacheKey.FleetOfferRents, vehicleId, showLinkedToContract, showCancelled, searchValue);

  const { data, isLoading } = useDropdownCache<DataWithPagination<FleetVehicleOfferRentRow>>(
    cacheKey,
    () => getFleetVehicleOfferRentsAPI(
      pagination,
      {
        vehicleId,
        showLinkedToContract,
        showCancelled,
        search: searchValue || undefined,
        showItems: false,
        showVehicle: false,
        showStatus: false,
        showPdfUpload: false,
      },
    ),
    5 * 60 * 1000
  );

  const searchResults = data ?? initialDataWithPagination;

  useEffect(() => {
    autoSelectedRef.current = false;
  }, [vehicleId]);

  useEffect(() => {
    if (data && onLoaded) onLoaded(data.data.length);
  }, [data]);

  const handleSearchChange = useCallback((val: string) => {
    setSearchValue(val);
  }, []);

  const handleSearchFocus = useCallback(() => {
    setSearchValue("");
  }, []);

  useEffect(() => {
    if (!autoSelectLast || autoSelectedRef.current || isLoading || value) return;
    if (searchResults.data.length === 0) return;

    const lastOffer = searchResults.data[0];
    autoSelectedRef.current = true;

    const syntheticItem: DropdownOption = {
      value: lastOffer.id,
      text: lastOffer.offerNumber || "",
    };
    const syntheticEvent = { target: { dataset: {} } };
    onChange(syntheticEvent, name, syntheticItem);
    if (onOfferSelect) onOfferSelect(lastOffer);
  }, [searchResults.data, isLoading, autoSelectLast, value]);

  const offerItems: DropdownOption[] = useMemo(() => searchResults.data.map((i) => {
    const clientName = i.contactClient?.name || i.contactClient?.companyName || "";
    return {
      value: i.id,
      text: (<div className="flex items-center justify-between w-full">
        <span>{i.offerNumber} </span>
        <span className="text-xs bg-blue-200 text-blue-900 py-0.5 px-2 rounded-sm block ml-2.5">{clientName}</span>
      </div>),
    };
  }), [searchResults.data]);

  const handleChange = (e: any, fieldName: string, item: DropdownOption) => {
    onChange(e, fieldName, item);

    if (onOfferSelect) {
      const selectedOffer = searchResults.data.find((offer) => offer.id === item.value) || null;
      onOfferSelect(selectedOffer);
    }
  };

  return (
    <DropdownAutocomplete
      label={label}
      name={name}
      disabled={disabled}
      required={required}
      placeholder={placeholder}
      value={value}
      direction={direction}
      width={width}
      height={height}
      options={offerItems}
      onChange={handleChange}
      onSearchChange={handleSearchChange}
      onSearchFocus={handleSearchFocus}
      error={error}
      isLoading={isLoading}
    />
  );
};

export default React.memo(FleetOfferRentDropdown);
